import axios from 'axios';
import { DEFAULT_ACCESS_OUTPUT } from './accessOutput';
import { creditConfirmation, planCreditQuote } from './planCreditPolicy';
import { WEB_PLAYER_OPTION_ID } from './partnerApps';

// Match the existing server policy: Web Player trials require two connections.
export function provisionScreenCount(screens, isTrial = false, partnerAppId = '') {
  const requested = Number(screens) || 1;
  return isTrial && partnerAppId === WEB_PLAYER_OPTION_ID ? Math.max(2, requested) : requested;
}

/**
 * Cria linha IPTV a partir de um plano do catálogo (pacote Servidor já montado).
 * Não cria bouquets novos no painel.
 */
export async function createLineFromPlan(
  apiUrl,
  {
    planId,
    nome,
    whatsapp,
    isTrial = false,
    duracaoId,
    telas = 1,
    notes,
    contactId,
    credentialFormat,
    username,
    password,
    contentMode,
    partnerAppId,
    creditPolicy,
  } = {},
) {
  const payload = {
    package_id: String(planId || ''),
    catalog_plan_id: String(planId || ''),
    is_trial: !!isTrial,
    whatsapp: String(whatsapp || '').replace(/\D/g, ''),
    notes: String(notes || nome || '').trim(),
    nome: String(nome || '').trim(),
    max_connections: provisionScreenCount(telas, isTrial, partnerAppId),
    credit_confirmation: creditConfirmation(creditPolicy, telas),
    // Criação sempre libera ambos. Alterações manuais pertencem à edição da linha.
    output_format: DEFAULT_ACCESS_OUTPUT,
    access_output: DEFAULT_ACCESS_OUTPUT,
    use_quick_test_template: !!isTrial,
  };
  if (duracaoId) payload.duracao_id = duracaoId;
  if (contactId) payload.contact_id = contactId;
  if (credentialFormat) payload.credentialFormat = credentialFormat;
  if (username) payload.username = username;
  if (password) payload.password = password;
  const mode = String(contentMode || '').trim().toLowerCase();
  if (mode === 'adult' || mode === 'family') payload.content_mode = mode;
  if (partnerAppId) payload.partner_app_id = String(partnerAppId);

  const { data } = await axios.post(`${apiUrl}/api/xui/lines`, payload, {
    withCredentials: true,
    timeout: 120000,
  });
  return data;
}

export function isTrialCatalogPlan(plan, pkg) {
  // Explicit commercial metadata wins over a legacy supplier trial package.
  // This classification only guides the UI; the server validates every quote.
  const flag = String(plan?.is_trial ?? '').trim().toLowerCase();
  if (['true', '1'].includes(flag)) return true;
  if (['false', '0'].includes(flag)) return false;
  if (planCreditQuote(plan?.credit_policy, plan?.credit_policy?.included_screens, 12)) return false;
  if (['true', '1'].includes(String(pkg?.is_trial ?? '').trim().toLowerCase())) return true;
  const blob = `${plan?.label || ''} ${plan?.package_name || ''} ${pkg?.package_name || ''}`.toLowerCase();
  return /\b(teste|trial)\b/.test(blob);
}

export function formatPlanPrice(value, locale = 'pt-BR') {
  const n = Number(String(value ?? '').replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  const loc = locale === 'en' || String(locale).startsWith('en')
    ? 'en-US'
    : locale === 'es' || String(locale).startsWith('es')
      ? 'es-ES'
      : 'pt-BR';
  return n.toLocaleString(loc, { style: 'currency', currency: 'BRL' });
}

function foldUnit(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function formatDurationParts(n, unitRaw, t) {
  const u = foldUnit(unitRaw);
  if (/^(h|hr|hrs|hora|horas|hour|hours)$/.test(u)) return t('plan.hours', { n });
  if (/^(min|mins|minuto|minutos|minute|minutes)$/.test(u)) return t('plan.min', { n });
  if (/^(d|dia|dias|day|days)$/.test(u)) return n === 1 ? t('plan.day1') : t('plan.dayN', { n });
  if (/^(mes|meses|month|months)$/.test(u)) return n === 1 ? t('plan.month1') : t('plan.monthN', { n });
  return `${n} ${unitRaw}`.trim();
}

export function formatXuiPkgDuration(pkg, { trial = false, t } = {}) {
  if (!pkg) return '';
  const isTrial = trial || pkg.is_trial === true || pkg.is_trial === 1;
  const n = Number(isTrial ? pkg.trial_duration : pkg.official_duration) || 0;
  const unit = String(
    (isTrial ? pkg.trial_duration_in : pkg.official_duration_in) || (isTrial ? 'hours' : 'days'),
  ).toLowerCase();
  if (!(n > 0)) return '';
  if (typeof t === 'function') return formatDurationParts(n, unit, t);
  const unitMap = {
    hour: 'h',
    hours: 'h',
    day: n === 1 ? ' dia' : ' dias',
    days: n === 1 ? ' dia' : ' dias',
    month: n === 1 ? ' mês' : ' meses',
    months: n === 1 ? ' mês' : ' meses',
    minute: ' min',
    minutes: ' min',
  };
  return `${n}${unitMap[unit] || ` ${unit}`}`;
}

export function localizeDurationLabel(label, t) {
  const s = String(label || '').trim();
  if (!s) return '';
  if (typeof t !== 'function') return s;
  const m = s.match(
    /^(\d+)\s*(h|hr|hrs|hora|horas|hours?|d|dia|dias|days?|m[eê]s|meses|months?|min|mins|minutos?|minutes?)\.?$/i,
  );
  if (!m) return s;
  return formatDurationParts(Number(m[1]), m[2], t);
}

export function localizeConnectionsLabel(label, t) {
  const s = String(label || '').trim();
  if (!s) return '';
  if (typeof t !== 'function') return s;
  const m = s.match(/^(\d+)\s*(tela|telas|screen|screens|pantalla|pantallas)\.?$/i);
  if (!m) return s;
  const n = Number(m[1]);
  return n === 1 ? t('qt.screen1') : t('qt.screenN', { n });
}

export function localizeCatalogPlanLabel(label, t) {
  const s = String(label || '').trim();
  if (!s || typeof t !== 'function') return s;
  const parts = s.split('|').map((x) => x.trim());
  const head = parts[0] || '';
  const named = head.match(/^(?:plano de|plan de)\s+(.+)$/i);
  const enNamed = !named && head.match(/^(.+?)\s+plan$/i);
  const durRaw = named ? named[1].trim() : enNamed ? enNamed[1].trim() : '';
  if (durRaw && parts.length === 2) {
    const dur = localizeDurationLabel(durRaw, t);
    const screens = localizeConnectionsLabel(parts[1], t);
    return t('plan.named', { dur, screens });
  }
  return s;
}

/** Opt-in explícito: só aparece no Teste Rápido / dashboard se a flag estiver ligada. */
export function isQuickTestVisible(plan) {
  const v = plan?.show_in_quick_test;
  if (v === false || v === 0) return false;
  const s = String(v ?? '').trim().toLowerCase();
  if (s === 'false' || s === '0' || s === 'off' || s === 'no') return false;
  return v === true || v === 1 || s === 'true' || s === '1' || s === 'on' || s === 'yes';
}

export function screenOptions(max = 5) {
  const n = Math.max(1, Math.min(12, Number(max) || 5));
  return Array.from({ length: n }, (_, i) => i + 1);
}

/** Duração do plano comercial → id (ex.: "PLANO [15 DIAS]" → d_15d). */
export function durationIdFromPlan(plan) {
  if (!plan) return '';
  const blob = `${plan.duration_label || ''} ${plan.label || ''} ${plan.package_name || ''}`;
  const daysM = blob.match(/(\d+)\s*dias?/i);
  if (daysM) {
    const d = Number(daysM[1]);
    if (d === 7) return 'd_7d';
    if (d === 15) return 'd_15d';
    if (d === 30) return 'd_1m';
    if (d === 90) return 'd_3m';
    if (d === 180) return 'd_6m';
    if (d === 365 || d === 360) return 'd_12m';
  }
  if (/\b1\s*m[eê]s\b/i.test(blob) || /\b1\s*month\b/i.test(blob)) return 'd_1m';
  if (/\b3\s*m/i.test(blob)) return 'd_3m';
  if (/\b6\s*m/i.test(blob)) return 'd_6m';
  if (/\b12\s*m/i.test(blob)) return 'd_12m';
  return '';
}
